import { useRef, useEffect, memo, useCallback } from "react"
import gsap from "gsap"
import { discardTrucoCard } from "../../utils/discardTrucoCard"
import { CARD_BACK_NOTATION } from "../../utils/TrucoGameUtils"

const HOLD_DELAY = 180          // ms — hold timer before drag mode activates
const DRAG_THRESHOLD = 6        // px — pointer must move this far to commit to a drag

export const CardHand = memo(({
    cards, totalSeats, seatIndex, isMe = true,
    isMyTurn, clearActionButtons, sendPlayerAction,
    seatNumber, dispatch, getCardPosition, getCenterCardPos, isCardsDealing
}) => {
    const containerRef = useRef(null)
    const cardRefs = useRef({})
    const clickOrderRef = useRef(0)
    const isPlayingCardRef = useRef(false)
    const rafRef = useRef(null)
    // Center-card rect in client/screen space (set once per drag, free to hit-test)
    const centerRectRef = useRef(null)
    // Is the dragged card currently hovering over the drop zone?
    const overDropZoneRef = useRef(false)

    const drag = useRef({
        active: false,
        holding: false,
        committed: false,
        holdTimer: null,
        pointerId: null,
        startClientX: 0,
        startClientY: 0,
        startLocalY: 0,
        startLocalX: 0,
        grabbedOffsetX: 0,
        grabbedOffsetY: 0,
        index: null,
        cardId: null,

        currentLocalX: 0,
        currentLocalY: 0,
        pendingClientX: 0,
        pendingClientY: 0,
        // rotation interpolation
        slotRotations: [],
        slotXPositions: [],
    })

    // ─── Re-position cards when hand / orientation changes ─────────────────
    useEffect(() => {
        if (isCardsDealing) {
            cards.forEach((card, index) => {
                const el = cardRefs.current[card.id]
                if (!el || el.dataset.animating === "true") return
                const { x, y, rotation, originalX, originalY } = getSlot(index, card.cardValue)
                gsap.set(el, { x, y, rotate: rotation, zIndex: 997 + index })
                // gsap.fromTo(el, { x: originalX, y: originalY }, { x, y, rotate: rotation, zIndex: 997 + index })
                document.querySelector(`#truco-card-${seatIndex}${index}`)?.remove()
            })
        } else {
            cards.forEach((card, index) => {
                const el = cardRefs.current[card.id]
                if (!el) return
                if (drag.current.cardId === card.id) return   // don't snap the dragged card
                if (el.dataset.animating === "true") return
                const { x, y, rotation } = getSlot(index, card.cardValue)
                gsap.to(el, { x, y, rotate: rotation, zIndex: 997 + index, duration: 0.25, ease: "power3.out" })
            })
        }
    }, [cards, isCardsDealing, getCardPosition]) // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Window-level listeners (track pointer anywhere on screen) ──────────
    useEffect(() => {
        window.addEventListener("pointermove", handlePointerMove, { passive: false })
        window.addEventListener("pointerup", handlePointerUp)
        return () => {
            window.removeEventListener("pointermove", handlePointerMove)
            window.removeEventListener("pointerup", handlePointerUp)
        }
    }, [cards, isMyTurn]) // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Helpers ────────────────────────────────────────────────────────────

    const getScale = () => {
        const gameBox = document.querySelector("#gameBoxMain")
        if (!gameBox) return 1
        const m = gameBox.style.transform.match(/scale\(([^)]+)\)/)
        return m ? parseFloat(m[1]) : 1
    }

    const getSlot = (index, cardValue = "") => {
        const { x, y, widthGap, heightGap, rotation } = getCardPosition(seatIndex, cardValue)
        const count = cards.length || 3
        const step = rotation   // degrees applied to left/right cards
        if (seatIndex === 0) {
            if (count === 3) {
                if (index === 0) return { x: x - widthGap, y: y - heightGap, rotation: step, originalX: x - (widthGap / 2), originalY: y + heightGap }
                if (index === 1) return { x, y, rotation: 0, originalX: x + (widthGap / 2), originalY: y + heightGap }
                if (index === 2) return { x: x + widthGap, y: y - heightGap, rotation: -step, originalX: x, originalY: y }
            }
            if (count === 2) {
                const half = widthGap / 2
                if (index === 0) return { x: x - half, y: y + heightGap, rotation: step, originalX: x, originalY: y }
                if (index === 1) return { x: x + half, y: y + heightGap, rotation: -step, originalX: x, originalY: y }
            }
        }
        if (seatIndex / totalSeats < 0.5) {
            if (count === 3) {
                if (index === 0) return { x: x - heightGap, y: y - widthGap, rotation: - 90 - step, originalX: x - (widthGap / 2), originalY: y + heightGap }
                if (index === 1) return { x, y, rotation: -90, originalX: x + (widthGap / 2), originalY: y + heightGap }
                if (index === 2) return { x: x - heightGap, y: y + widthGap, rotation: step - 90, originalX: x, originalY: y }
            }
            if (count === 2) {
                const half = widthGap / 2
                if (index === 0) return { x: x - heightGap, y: y - half, rotation: - 90 - step, originalX: x, originalY: y }
                if (index === 1) return { x: x - heightGap, y: y + half, rotation: - 90 + step, originalX: x, originalY: y }
            }
        }

        if (seatIndex / totalSeats > 0.5) {
            if (count === 3) {
                if (index === 0) return { x: x + heightGap, y: y + widthGap, rotation: 90 - step, originalX: x - (widthGap / 2), originalY: y + heightGap }
                if (index === 1) return { x, y, rotation: 90, originalX: x + (widthGap / 2), originalY: y + heightGap }
                if (index === 2) return { x: x + heightGap, y: y - widthGap, rotation: step + 90, originalX: x, originalY: y }
            }
            if (count === 2) {
                const half = widthGap / 2
                if (index === 0) return { x: x + heightGap, y: y - half, rotation: 90 + step, originalX: x, originalY: y }
                if (index === 1) return { x: x + heightGap, y: y + half, rotation: 90 - step, originalX: x, originalY: y }
            }
        }
        if (seatIndex / totalSeats === 0.5) {
            if (count === 3) {
                if (index === 0) return { x: x - widthGap, y: y + heightGap, rotation: -step, originalX: x - (widthGap / 2), originalY: y + heightGap }
                if (index === 1) return { x, y, rotation: 0, originalX: x + (widthGap / 2), originalY: y + heightGap }
                if (index === 2) return { x: x + widthGap, y: y + heightGap, rotation: step, originalX: x, originalY: y }
            }
            if (count === 2) {
                const half = widthGap / 2
                if (index === 0) return { x: x - half, y: y + heightGap, rotation: -step, originalX: x, originalY: y }
                if (index === 1) return { x: x + half, y: y + heightGap, rotation: step, originalX: x, originalY: y }
            }
        }
        return { x, y, rotation: 0, originalX: x, originalY: y }
    }

    const getMidpoints = () => [
        (getSlot(0).x + getSlot(1).x) / 2,
        cards.length === 3 ? (getSlot(1).x + getSlot(2).x) / 2 : Infinity,
    ]

    const getRotationForX = (cardCenterX) => {
        const rotations = drag.current.slotRotations
        const slotXs = drag.current.slotXPositions
        const count = slotXs.length

        if (count === 0) return 0
        if (count === 1) return rotations[0]

        // Clamp to the slot range
        if (cardCenterX <= slotXs[0]) return rotations[0]
        if (cardCenterX >= slotXs[count - 1]) return rotations[count - 1]

        // Find the segment the card is currently in and lerp
        for (let i = 0; i < count - 1; i++) {
            if (cardCenterX >= slotXs[i] && cardCenterX <= slotXs[i + 1]) {
                const t = (cardCenterX - slotXs[i]) / (slotXs[i + 1] - slotXs[i])
                return rotations[i] + t * (rotations[i + 1] - rotations[i])
            }
        }
        return 0
    }

    // Whether the dragged card's bounding rect overlaps the drop-zone rect.
    // Both rects are in client/screen space.
    // `cardEl`  — the card DOM element currently being dragged
    // We use getBoundingClientRect() on the card because GSAP transforms it
    // and the browser always returns the post-transform rect.
    const isCardOverDropZone = (cardEl) => {
        const r = centerRectRef.current
        if (!r || !cardEl) return false
        const c = cardEl.getBoundingClientRect()
        // AABB overlap: two rects overlap if neither is fully outside the other
        return c.right > r.left &&
            c.left < r.right &&
            c.bottom > r.top &&
            c.top < r.bottom
    }

    // ─── Cleanup ─────────────────────────────────────────────────────────────
    const cleanupDrag = useCallback((el) => {
        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
        if (el && drag.current.pointerId !== null) {
            try { el.releasePointerCapture(drag.current.pointerId) } catch (_) { }
        }
        if (el) el.style.cursor = "grab"
        // hideDropZone(seatIndex)
        overDropZoneRef.current = false
        centerRectRef.current = null
        drag.current.active = false
        drag.current.holding = false
        drag.current.committed = false
        drag.current.pointerId = null
        drag.current.index = null
        drag.current.cardId = null
    }, [seatIndex])

    // ─── rAF frame: move card + update drop-zone highlight ──────────────────
    const applyDragFrame = useCallback(() => {
        rafRef.current = null
        if (!drag.current.holding || !drag.current.committed) return

        const { pendingClientX, pendingClientY, grabbedOffsetX, grabbedOffsetY, cardId } = drag.current
        const el = cardRefs.current[cardId]
        if (!el) return
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return
        const scale = getScale()

        const localX = (pendingClientX - rect.left) / scale
        const localY = pendingClientY / scale
        const newX = localX - grabbedOffsetX
        const newY = localY - grabbedOffsetY

        const cardHalfWidth = (el.offsetWidth || 0) / 2
        const cardCenterX = newX + cardHalfWidth
        const targetRotation = getRotationForX(cardCenterX)

        gsap.set(el, { x: newX, y: newY, rotate: targetRotation })

        drag.current.currentLocalX = localX
        drag.current.currentLocalY = localY

        // ── Drop-zone highlight ──────────────────────────────────────────
        const over = isCardOverDropZone(el)
        if (over !== overDropZoneRef.current) {
            overDropZoneRef.current = over
            // if (centerRectRef.current) showDropZone(centerRectRef.current, over, seatIndex)
        }
    }, [seatIndex]) // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Pointer handlers ───────────────────────────────────────────────────

    const handlePointerDown = useCallback((e, index) => {
        if (!isMe) return
        if (drag.current.active) return
        if (e.button !== undefined && e.button !== 0) return
        e.preventDefault()

        const el = cardRefs.current[cards[index]?.id]
        if (!el) return

        drag.current.active = true
        drag.current.holding = false
        drag.current.committed = false
        drag.current.index = index
        drag.current.cardId = cards[index]?.id
        drag.current.startClientX = e.clientX
        drag.current.startClientY = e.clientY
        drag.current.pointerId = e.pointerId

        // Capture immediately — all future move/up events come here even off-card
        el.setPointerCapture(e.pointerId)

        drag.current.holdTimer = setTimeout(() => {
            if (!drag.current.active) return
            const rect = containerRef.current?.getBoundingClientRect()
            if (!rect) return
            const scale = getScale()
            const localX = (drag.current.startClientX - rect.left) / scale
            const localY = drag.current.startClientY / scale
            const { x, y } = getSlot(index)

            drag.current.holding = true
            drag.current.startLocalX = localX
            drag.current.startLocalY = localY
            drag.current.grabbedOffsetX = localX - x
            drag.current.grabbedOffsetY = localY - y
            drag.current.currentLocalX = localX
            drag.current.currentLocalY = localY
            drag.current.pendingClientX = drag.current.startClientX
            drag.current.pendingClientY = drag.current.startClientY

            drag.current.slotRotations = Array.from({ length: cards.length }, (_, i) => getSlot(i).rotation)
            drag.current.slotXPositions = Array.from({ length: cards.length }, (_, i) => getSlot(i).x)

            el.style.cursor = "grabbing"
            gsap.to(el, { scale: 1.05, zIndex: 2000, duration: 0.15 })

            // Pre-fetch center card rect for the entire drag (hit-tests are then O(1))
            if (isMyTurn) {
                const centerPos = getCenterCardPos(seatIndex, cards[index]?.cardValue ?? "", 4 - cards.length)
                if (centerPos) {
                    const gameBox = document.querySelector("#gameBoxMain")
                    const gbRect = gameBox?.getBoundingClientRect()
                    if (gbRect) {
                        const s = getScale()
                        centerRectRef.current = {
                            left: gbRect.left + centerPos.x * s,
                            top: gbRect.top + centerPos.y * s,
                            right: gbRect.left + (centerPos.x + centerPos.width) * s,
                            bottom: gbRect.top + (centerPos.y + centerPos.height) * s,
                            width: centerPos.width,
                            height: centerPos.height,
                            x: centerPos.x,
                            y: centerPos.y
                        }
                        // showDropZone(centerRectRef.current, false)
                    }
                }
            }
        }, HOLD_DELAY)
    }, [cards, isMe, isMyTurn]) // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Pointer Move ────────────────────────────────────────────────────────
    const handlePointerMove = useCallback((e) => {
        if (!drag.current.active) return
        e.preventDefault()

        // Wait until pointer has moved past threshold AND hold timer has fired
        if (!drag.current.committed) {
            const dx = e.clientX - drag.current.startClientX
            const dy = e.clientY - drag.current.startClientY
            if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return
            if (!drag.current.holding) return
            drag.current.committed = true
        }

        drag.current.pendingClientX = e.clientX
        drag.current.pendingClientY = e.clientY

        if (!rafRef.current) {
            rafRef.current = requestAnimationFrame(applyDragFrame)
        }

        // Reorder cards while dragging
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return
        const scale = getScale()
        const localX = (e.clientX - rect.left) / scale
        const el = cardRefs.current[drag.current.cardId]
        const cardHalfWidth = el ? (el.offsetWidth || 0) / 2 : 0
        const newX = localX - drag.current.grabbedOffsetX
        const cardCenterX = newX + cardHalfWidth

        const [mid01, mid12] = getMidpoints()
        const draggedIndex = cards.findIndex(c => c.id === drag.current.cardId)
        if (draggedIndex === -1) return

        let projected = 0
        if (cards.length === 3) {
            if (cardCenterX > mid12) projected = 2
            else if (cardCenterX > mid01) projected = 1
        } else if (cards.length === 2) {
            if (cardCenterX > mid01) projected = 1
        }
        projected = Math.min(projected, cards.length - 1)

        if (projected !== draggedIndex) {
            let newOrder = [...cards]
            const [moved] = newOrder.splice(draggedIndex, 1)
            newOrder.splice(projected, 0, moved)
            newOrder = newOrder.map((c, i) => ({ ...c, cardIndex: i }))
            drag.current.index = projected
            drag.current.slotRotations = Array.from({ length: newOrder.length }, (_, i) => getSlot(i).rotation)
            drag.current.slotXPositions = Array.from({ length: newOrder.length }, (_, i) => getSlot(i).x)
            dispatch({ type: "REORDER_HAND", payload: { seatIndex, newOrder } })
        }
    }, [cards, applyDragFrame, dispatch, seatIndex]) // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Pointer Up ──────────────────────────────────────────────────────────
    const handlePointerUp = useCallback((e) => {
        clearTimeout(drag.current.holdTimer)
        if (!drag.current.active) return

        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null }

        const index = drag.current.index
        const cardId = drag.current.cardId
        const el = cardRefs.current[cardId]

        if (el && drag.current.pointerId !== null) {
            try { el.releasePointerCapture(drag.current.pointerId) } catch (_) { }
        }

        // ── TAP: hold timer never fired — treat as click-to-discard ──────
        if (!drag.current.holding) {
            cleanupDrag(el)
            if (isMe && isMyTurn && !isPlayingCardRef.current) {
                const card = cards.find(c => c.id === cardId)
                if (card && el && !el.dataset.clicked) {
                    const { x, y } = getSlot(index)
                    handleDiscardCards(el, x, y, card.cardValue, card.cardIndex, card.id)
                }
            }
            return
        }

        // From here the card was actively being dragged
        const card = cards.find(c => c.id === cardId)
        if (!card || !el || el.dataset.clicked) { cleanupDrag(el); return }

        // ── Card rect overlaps the drop zone → discard ────────────────────
        // We do one final overlap check at release using the card's current
        // getBoundingClientRect (post-GSAP transform) vs the stored drop-zone
        // rect.  This handles both:
        //   • slow drag-and-place onto the zone
        //   • fast throw where the card happens to be over the zone at release
        // Anything else (dragged to top of screen, wrong area, etc.) snaps back.
        if (isMe && isMyTurn && !isPlayingCardRef.current && isCardOverDropZone(el)) {
            const { x, y } = getSlot(index)
            cleanupDrag(el)
            handleDiscardCards(el, x, y, card.cardValue, card.cardIndex, card.id)
            return
        }

        // ── Snap back — card was not over the drop zone ───────────────────
        const { x, y, rotation } = getSlot(index)
        gsap.to(el, { x, y, rotate: rotation, scale: 1, zIndex: 997 + index, duration: 0.28, ease: "power3.out" })
        cleanupDrag(el)
    }, [cards, isMe, isMyTurn, cleanupDrag]) // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Discard ────────────────────────────────────────────────────────────

    const handleDiscardCards = (card, x, y, cardValue, cardIndex, cardId) => {
        if (card.dataset.clicked) return
        if (isPlayingCardRef.current) return  // already throwing a card
        isPlayingCardRef.current = true        // lock immediately

        clickOrderRef.current += 1
        const suit = cardValue.slice(-1)
        const value = cardValue.slice(0, -1)

        card.style.bottom = 0
        card.style.left = 0

        const animationCompleted = () => {
            card.dataset.clicked = "true"
            clearActionButtons()
            sendPlayerAction("PLAY_CARD", { cards: { suit, value }, seatNumber })
            dispatch({
                type: "MOVE_CARD_TO_CENTER",
                payload: { seatIndex, cardId, cardValue, trickCount: 4 - cards.length }
            })
            // Release the lock so the next trick's card can be played
            isPlayingCardRef.current = false
        }

        const cardWidth = parseFloat(getComputedStyle(card).getPropertyValue("--card-width"))
        const cardHeight = parseFloat(getComputedStyle(card).getPropertyValue("--card-height"))
        const { x: centerX, y: centerY, width: centerWidth, height: centerHeight } =
            getCenterCardPos(seatIndex, cardValue, 4 - cards.length)

        const scaleX = centerWidth / cardWidth
        const scaleY = centerHeight / cardHeight
        let finalCenterX = centerX
        let finalCenterY = centerY
        // if (scaleX - 1) finalCenterX += centerWidth * (scaleX - 1) / 2
        // if (scaleY - 1) finalCenterY -= centerHeight * (scaleY - 1) / 2
        card.style.transformOrigin = "bottom left"
        discardTrucoCard({
            card,
            totalSeats,
            seatIndex,
            cardX: x,
            cardY: y,
            addOnIndex: clickOrderRef.current,
            centerCardX: finalCenterX,
            centerCardY: finalCenterY,
            scaleX,
            scaleY,
            animationCompleted
        })
    }

    // ─── Render ──────────────────────────────────────────────────────────────

    return (
        <div
            className="truco-cards-container"
            ref={containerRef}
        // onPointerMove={handlePointerMove}
        // onPointerUp={handlePointerUp}
        >
            <>
                {/* {Array.from({ length: 3 }, (_, ind) => (seatIndex === totalSeats / 2 ? { cardValue: "AE", id: `AE-${ind}`, cardIndex: ind } : { cardValue: "XX", id: `XX-${ind}`, cardIndex: ind })).map((card, index) => { */}
                {cards.map((card, index) => {
                    const { width, height, backgroundPosition: { backgroundPosition, spriteWidth, spriteHeight } } = getCardPosition(seatIndex, card.cardValue)
                    const { x, y, rotation } = getSlot(index, card.cardValue)
                    const [imageLeft, imageTop] = backgroundPosition.split(" ")
                    return (
                        <div
                            key={card.id}
                            id={card.id}
                            ref={el => {
                                if (el) cardRefs.current[card.id] = el
                                else delete cardRefs.current[card.id]
                            }}
                            className={`truco-card${card.cardValue === CARD_BACK_NOTATION ? " truco-card-back" : ""}`}
                            style={{
                                zIndex: 997 + index,
                                backgroundPosition,
                                bottom: 0,
                                left: 0,
                                "--card-width": `${width}px`,
                                "--card-height": `${height}px`,
                                cursor: isMe ? "grab" : undefined,
                                transform: `translate(${x}px, ${y}px) rotate(${rotation}deg)`,
                                backgroundSize: `${spriteWidth}px ${spriteHeight}px`,
                                touchAction: "none"
                            }}
                            onPointerDown={(e) => handlePointerDown(e, index)}

                        />
                    )
                })}
                {/* {Boolean(centerRectRef.current) && Boolean(isMe || totalSeats / seatIndex === 2) && Boolean(drag.current.active) && < div className="truco-drop-zone" style={{ width: `${centerRectRef.current.width}px`, height: `${centerRectRef.current.height}px`, transform: `translate(${centerRectRef.current.x}px,${centerRectRef.current.y}px)` }} />} */}
            </>
        </div >
    )
})